import React, { useState, useEffect, useMemo } from 'react';
import WideDialog from '../common/WideDialog';
import ProjectManagerSearch from '../projects/ProjectManagerSearch';
import SearchableDropdown from '../common/SearchableDropdown';
import { US_STATES } from '../../data/locationData';
import { useDispatch, useSelector } from 'react-redux';
import { updateProject, uploadProjectAttachments, deleteProjectAttachment, fetchProjectDocuments } from '../../store/slices/projectsSlice';
import { showToast } from '../utils/Toast';
import RichTextEditor from '../common/RichTextEditor';
import ProposalsTable from '../proposals/ProposalsTable';
import ChangeOrderDialog from './ChangeOrderDialog';
import { fetchProposals } from '../../store/slices/proposalsSlice';
import Spinner from '../common/Spinner';
import { 
  fetchChangeOrders, 
  createChangeOrder, 
  updateChangeOrderStatus, 
  deleteChangeOrder, 
  fetchChangeOrder,
  setCurrentChangeOrder
} from '../../store/slices/changeOrdersSlice';
import PermitsTab from '../permits/PermitsTab';
import { fetchProjectProposals } from '../../store/slices/projectProposalsSlice';
import { createProposal } from '../../store/slices/proposalsSlice';
import ConfirmDialog from '../common/ConfirmDialog';
import MultiSelectDropdown from '../common/MultiSelectDropdown';
import { fetchCities } from '../../store/slices/citiesSlice';
import FinancialTab from './financial/FinancialTab';

const ImagePreviewDialog = ({ isOpen, onClose, imageUrl }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative max-w-4xl max-h-[90vh] overflow-auto">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img 
          src={imageUrl} 
          alt="Preview" 
          className="max-w-full max-h-[90vh] object-contain"
        />
      </div>
    </div>
  );
};

const TransactionsTable = ({ transactions, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Spinner size="xl" color="blue" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {transactions.map((transaction, index) => (
            <tr key={transaction.id || index}>
              <td className="px-6 py-4 whitespace-nowrap">
                {new Date(transaction.date).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {transaction.description}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                ${transaction.amount.toFixed(2)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                  ${transaction.type === 'payment' ? 'bg-green-100 text-green-800' : 
                    transaction.type === 'invoice' ? 'bg-blue-100 text-blue-800' : 
                    'bg-gray-100 text-gray-800'}`}
                >
                  {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                  ${transaction.status === 'approved' ? 'bg-green-100 text-green-800' : 
                    transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                    transaction.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'}`}
                >
                  {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const getStatusBadgeClass = (status) => {
  const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
  switch (status?.toLowerCase()) {
    case 'draft':
      return `${baseClasses} bg-gray-100 text-gray-700`;
    case 'released':
      return `${baseClasses} bg-blue-100 text-blue-700`;
    case 'not_released':
      return `${baseClasses} bg-yellow-100 text-yellow-700`;
    case 'approved_proposal_owner':
      return `${baseClasses} bg-green-100 text-green-700`;
    case 'rejected_proposal_owner':
      return `${baseClasses} bg-red-100 text-red-700`;
    case 'customer_rejected':
      return `${baseClasses} bg-red-100 text-red-700`;
    case 'customer_approved':
      return `${baseClasses} bg-emerald-100 text-emerald-700`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-700`;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount) => {
  if (!amount) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const formatStatus = (status) => {
  if (!status) return '';
  
  const statusMap = {
    'draft': 'Draft',
    'released': 'Released',
    'not_released': 'Not Released',
    'approved_proposal_owner': 'Approved (Pending)',
    'rejected_proposal_owner': 'Rejected',
    'customer_rejected': 'Customer Rejected',
    'customer_approved': 'Customer Approved'
  };

  return statusMap[status] || status.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ProjectDetailsDialog = ({ isOpen, onClose, project }) => {
  const dispatch = useDispatch();
  const { currentProject, loading } = useSelector(state => state.projects);
  const { proposals: projectProposals = [], loading: proposalsLoading } = useSelector(state => state.projectProposals || {});
  const { changeOrders: projectChangeOrders = [], loading: changeOrdersLoading = false } = useSelector(state => state.changeOrders || {});
  const { currentChangeOrder } = useSelector(state => state.changeOrders);
  const cities = useSelector(state => state.cities?.items || []);
  const citiesLoading = useSelector(state => state.cities?.loading);
  const citiesError = useSelector(state => state.cities?.error);

  const [activeTab, setActiveTab] = useState('General');
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [showChangeOrder, setShowChangeOrder] = useState(false);
  const [editingChangeOrder, setEditingChangeOrder] = useState(null);
  const [showProposalDialog, setShowProposalDialog] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    changeOrder: null
  });
  const [isMaximized, setIsMaximized] = useState(true);
  const [isTabbedView, setIsTabbedView] = useState(false);
  const [sameBillingAsClient, setSameBillingAsClient] = useState(false);

  // Add city loading effect
  useEffect(() => {
    if (isOpen && !citiesLoading && cities.length === 0) {
      console.log('Fetching cities...');
      dispatch(fetchCities())
        .unwrap()
        .then(result => {
          console.log('Cities fetched successfully:', result);
        })
        .catch(error => {
          console.error('Error fetching cities:', error);
        });
    }
  }, [isOpen, citiesLoading, cities.length, dispatch]);

  // Add debugging useEffect for cities
  useEffect(() => {
    console.log('Current cities state:', cities);
    console.log('Cities loading:', citiesLoading);
    console.log('Cities error:', citiesError);
  }, [cities, citiesLoading, citiesError]);

  // Add cityOptions transformation
  const cityOptions = useMemo(() => {
    console.log('Transforming cities to options:', cities);
    return cities.map(city => ({
      label: `${city.name}, ${city.state}`,
      value: city.code,
      name: city.name,
      state: city.state,
      code: city.code
    }));
  }, [cities]);

  // Add city selection handlers
  const handleCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        city: selectedCity?.name || '',
        state: selectedCity?.state || ''
      }
    }));
  };

  const handleClientCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      client: {
        ...prev.client,
        address: {
          ...prev.client.address,
          city: selectedCity?.name || '',
          state: selectedCity?.state || ''
        }
      }
    }));
  };

  const handleSiteContactCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      siteContact: {
        ...prev.siteContact,
        address: {
          ...prev.siteContact.address,
          city: selectedCity?.name || '',
          state: selectedCity?.state || ''
        }
      }
    }));
  };

  // Toggle between tabbed and non-tabbed view
  const toggleView = () => {
    setIsTabbedView(prev => !prev);
  };

  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Effect to handle copying client data to billing contact when sameBillingAsClient is checked
  useEffect(() => {
    if (sameBillingAsClient) {
      setFormData(prev => ({
        ...prev,
        billingContact: {
          name: prev.client.name,
          phone: prev.client.cellPhone,
          email: prev.client.email,
          address: {
            street: prev.client.address.street,
            city: prev.client.address.city,
            state: prev.client.address.state,
            zipCode: prev.client.address.zipCode
          }
        }
      }));
    }
  }, [sameBillingAsClient]);

  // Update the initial state in ProjectDetailsDialog
  const [formData, setFormData] = useState({
    jobNumber: '',
    leadContact: {
      name: '',
      phone: '',
      email: ''
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    mapLink: '',
    projectManagers: [],
    client: {
      name: '',
      email: '',
      cellPhone: '',
      altPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    },
    billingContact: {
      name: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      }
    },
    siteContact: {
      name: '',
      email: '',
      cellPhone: '',
      altPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      mapLink: ''
    },
    projectStatus: '',
    priority: '',
    categories: [],
    notes: '',
    attachments: []
  });

  // Update the useEffect that handles project data
  useEffect(() => {
    if (currentProject) {
      setFormData({
        jobNumber: currentProject.jobNumber || '',
        leadContact: {
          name: currentProject.leadContact?.name || '',
          phone: currentProject.leadContact?.phone || '',
          email: currentProject.leadContact?.email || ''
        },
        address: {
          street: currentProject.address?.street || '',
          city: currentProject.address?.city || '',
          state: currentProject.address?.state || '',
          zipCode: currentProject.address?.zipCode || ''
        },
        mapLink: currentProject.mapLink || '',
        projectManagers: currentProject.projectManagers || [],
        client: {
          name: currentProject.client?.name || '',
          email: currentProject.client?.email || '',
          cellPhone: currentProject.client?.cellPhone || '',
          altPhone: currentProject.client?.altPhone || '',
          address: {
            street: currentProject.client?.address?.street || '',
            city: currentProject.client?.address?.city || '',
            state: currentProject.client?.address?.state || '',
            zipCode: currentProject.client?.address?.zipCode || ''
          }
        },
        billingContact: {
          name: currentProject.billingContact?.name || '',
          email: currentProject.billingContact?.email || '',
          phone: currentProject.billingContact?.phone || '',
          address: {
            street: currentProject.billingContact?.address?.street || '',
            city: currentProject.billingContact?.address?.city || '',
            state: currentProject.billingContact?.address?.state || '',
            zipCode: currentProject.billingContact?.address?.zipCode || ''
          }
        },
        siteContact: {
          name: currentProject.siteContact?.name || '',
          email: currentProject.siteContact?.email || '',
          cellPhone: currentProject.siteContact?.cellPhone || '',
          altPhone: currentProject.siteContact?.altPhone || '',
          address: {
            street: currentProject.siteContact?.address?.street || '',
            city: currentProject.siteContact?.address?.city || '',
            state: currentProject.siteContact?.address?.state || '',
            zipCode: currentProject.siteContact?.address?.zipCode || ''
          },
          mapLink: currentProject.siteContact?.mapLink || ''
        },
        projectStatus: currentProject.projectStatus || '',
        priority: currentProject.priority || '',
        categories: currentProject.categories || [],
        notes: currentProject.notes?.[0]?.content || '',
        attachments: currentProject.attachments || []
      });

      // Ensure projectManagers is always an array and contains valid data
      const managers = currentProject.projectManagers || [];
      setSelectedManagers(Array.isArray(managers) ? managers : []);
      
      // Check if billing contact appears to have the same data as client
      // If client and billing contact both exist and have the same name, consider them the same
      if (currentProject.client && currentProject.billingContact && 
          currentProject.billingContact.name && 
          currentProject.client.name === currentProject.billingContact.name) {
        setSameBillingAsClient(true);
      } else {
        setSameBillingAsClient(false);
      }
    }
  }, [currentProject]);

  // Load tab-specific data lazily to avoid duplicate requests.
  useEffect(() => {
    if (!currentProject?._id) {
      return;
    }

    if (activeTab === 'Proposals') {
      dispatch(fetchProjectProposals(currentProject._id));
    }

    if (activeTab === 'Change Orders') {
      dispatch(fetchChangeOrders(currentProject._id));
    }
  }, [activeTab, currentProject?._id, dispatch]);

  const handleManagerSelect = (manager) => {
    if (!manager) return;
    setSelectedManagers(prev => {
      // Check if manager already exists
      if (prev.some(m => m._id === manager._id)) {
        return prev;
      }
      return [...prev, manager];
    });
  };

  const handleManagerRemove = (managerId) => {
    if (!managerId) return;
    setSelectedManagers(prev => prev.filter(manager => manager._id !== managerId));
  };

  const handleSave = async () => {
    try {
      const updates = {
        jobNumber: formData.jobNumber,
        priority: formData.priority,
        projectStatus: formData.projectStatus,
        address: {
          street: formData.address?.street || '',
          city: formData.address?.city || '',
          state: formData.address?.state || '',
          zipCode: formData.address?.zipCode || ''
        },
        mapLink: formData.mapLink,
        projectManagers: selectedManagers.map(manager => manager._id),
        
        // Client info will be used to update/create Client document
        clientInfo: {
          name: formData.client?.name || '',
          email: formData.client?.email || '', // Make sure email is included
          phone: formData.client?.cellPhone || '',
          altPhone: formData.client?.altPhone || '',
          address: {
            street: formData.client?.address?.street || '',
            city: formData.client?.address?.city || '',
            state: formData.client?.address?.state || '',
            zipCode: formData.client?.address?.zipCode || ''
          }
        },
        
        // Site contact remains as embedded document
        siteContactInfo: {
          name: formData.siteContact?.name || '',
          email: formData.siteContact?.email || '',
          cellPhone: formData.siteContact?.cellPhone || '',
          altPhone: formData.siteContact?.altPhone || '',
          address: {
            street: formData.siteContact?.address?.street || '',
            city: formData.siteContact?.address?.city || '',
            state: formData.siteContact?.address?.state || '',
            zipCode: formData.siteContact?.address?.zipCode || ''
          }
        },
        
        // Billing contact information
        billingContact: {
          name: formData.billingContact?.name || '',
          email: formData.billingContact?.email || '',
          phone: formData.billingContact?.phone || '',
          address: {
            street: formData.billingContact?.address?.street || '',
            city: formData.billingContact?.address?.city || '',
            state: formData.billingContact?.address?.state || '',
            zipCode: formData.billingContact?.address?.zipCode || ''
          }
        },
        
        leadContact: {
          name: formData.leadContact?.name || '',
          phone: formData.leadContact?.phone || '',
          email: formData.leadContact?.email || ''
        },
        
        notes: formData.notes ? [{
          content: formData.notes,
          createdAt: new Date()
        }] : []
      };

      await dispatch(updateProject({
        id: currentProject._id,
        updates
      })).unwrap();
      
      showToast('Project updated successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      showToast(error.message || 'Error updating project', 'error');
    }
  };

  // Style classes
  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;

  const labelClasses = "block text-xs uppercase text-gray-500 mb-1";

  // Add these handlers for attachments
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      const result = await dispatch(uploadProjectAttachments({
        projectId: currentProject._id,
        files
      })).unwrap();

      // Update local state with new attachments
      setFormData(prev => ({
        ...prev,
        attachments: [
          ...(prev.attachments || []),
          ...result.attachments
        ]
      }));

      showToast('Files uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading files:', error);
      showToast(error.message || 'Error uploading files', 'error');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      // Check if attachment is from original lead
      const isOriginalLeadAttachment = currentProject.originalLead?.attachments?.some(
        att => att._id === attachmentId
      );

      if (isOriginalLeadAttachment) {
        showToast('Cannot delete attachments from original lead', 'warning');
        return;
      }

      await dispatch(deleteProjectAttachment({
        projectId: currentProject._id,
        attachmentId
      })).unwrap();

      // Update local state by removing the deleted attachment
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter(att => att._id !== attachmentId)
      }));

      showToast('Attachment deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting attachment:', error);
      showToast(error.message || 'Error deleting attachment', 'error');
    }
  };

  const renderAttachment = (attachment) => {
    const isOriginalLeadAttachment = currentProject.originalLead?.attachments?.some(
      att => att._id === attachment._id
    );

    const isImage = attachment.fileType ? 
      /\.(jpg|jpeg|png|gif)$/i.test(attachment.fileName) :
      attachment.type.startsWith('image/');
    
    const fileUrl = attachment.fileUrl || URL.createObjectURL(attachment);
    const fileName = attachment.fileName || attachment.name;
    
    if (isImage) {
      return (
        <div key={fileUrl} className="relative group">
          <img 
            src={fileUrl} 
            alt={fileName}
            className="w-32 h-32 object-cover rounded-lg cursor-pointer"
            onClick={() => setPreviewImage(fileUrl)}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center gap-2">
            <button
              onClick={() => setPreviewImage(fileUrl)}
              className="text-white opacity-0 group-hover:opacity-100 p-2 hover:bg-black hover:bg-opacity-20 rounded"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            {!isOriginalLeadAttachment && (
              <button
                onClick={() => handleDeleteAttachment(attachment._id)}
                className="text-white opacity-0 group-hover:opacity-100 p-2 hover:bg-black hover:bg-opacity-20 rounded"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={fileUrl} className="flex items-center justify-between gap-2 p-2 border rounded-lg">
        <div className="flex items-center gap-2">
          <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            {fileName}
          </a>
        </div>
        <button
          onClick={() => handleDeleteAttachment(attachment._id)}
          className="text-red-500 hover:text-red-700 p-1"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    );
  };

  // Add these helper functions at the top of the component
  const updateClientAddress = (field, value) => {
    setFormData(prev => ({
      ...prev,
      client: {
        ...prev.client,
        address: {
          ...prev.client.address,
          [field]: value
        }
      }
    }));
  };

  const updateSiteContactAddress = (field, value) => {
    setFormData(prev => ({
      ...prev,
      siteContact: {
        ...prev.siteContact,
        address: {
          ...prev.siteContact.address,
          [field]: value
        }
      }
    }));
  };
  
  const updateBillingContactAddress = (field, value) => {
    setFormData(prev => ({
      ...prev,
      billingContact: {
        ...prev.billingContact,
        address: {
          ...prev.billingContact.address,
          [field]: value
        }
      }
    }));
  };

  const updateAddress = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  const updateClient = (field, value) => {
    setFormData(prev => ({
      ...prev,
      client: {
        ...prev.client,
        [field]: value
      }
    }));
  };

  const updateSiteContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      siteContact: {
        ...prev.siteContact,
        [field]: value
      }
    }));
  };
  
  const updateBillingContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      billingContact: {
        ...prev.billingContact,
        [field]: value
      }
    }));
  };

  // Add these additional helper functions
  const updateLeadContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      leadContact: {
        ...prev.leadContact,
        [field]: value
      }
    }));
  };

  const updateSimpleField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add these style classes at the top level of the component
  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24
  `;

  // Add state for editable fields at the top level of the component
  const [editableFields, setEditableFields] = useState({
    city: false,
    state: false,
    clientCity: false,
    clientState: false,
    siteContactCity: false,
    siteContactState: false
  });

  // Add renderNonTabbedContent function to show all steps in one view
  const renderNonTabbedContent = () => {
    const labelClasses = "block text-xs uppercase text-gray-500 mb-1";

    return (
      <div className="space-y-12">
        {/* Project Information Section */}
        <div>
          <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Project Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
            <div>
              <label className={labelClasses}>Job Number</label>
              <input
                type="text"
                className={inputClasses}
                value={formData.jobNumber}
                onChange={(e) => updateSimpleField('jobNumber', e.target.value)}
                readOnly
              />
            </div>

            <div>
              <label className={labelClasses}>Lead Contact Name</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter contact name"
                value={formData.leadContact.name}
                onChange={(e) => updateLeadContact('name', e.target.value)}
              />
            </div>
            
            <div>
              <label className={labelClasses}>Contact Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.leadContact.phone}
                onChange={(e) => updateLeadContact('phone', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Contact Email</label>
              <input
                type="email"
                className={inputClasses}
                placeholder="Enter email address"
                value={formData.leadContact.email}
                onChange={(e) => updateLeadContact('email', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>City</label>
              <SearchableDropdown
                key="project-city-dropdown-nonTabbed"
                options={cityOptions}
                value={cityOptions.find(opt => opt.name === formData.address.city) || ''}
                onChange={handleCityChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                placeholder="Search city..."
                className={inputClasses}
                onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, city: isEditable}))}
                isEditable={editableFields.city}
              />
            </div>

            <div>
              <label className={labelClasses}>Assigned To</label>
              <MultiSelectDropdown
                selectedManagers={selectedManagers}
                onManagerSelect={handleManagerSelect}
                onManagerRemove={handleManagerRemove}
              />
            </div>

            <div>
              <label className={labelClasses}>Project Status</label>
              <select
                className={inputClasses}
                value={formData.projectStatus}
                onChange={(e) => updateSimpleField('projectStatus', e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="active">Active</option>
                <option value="ready_to_schedule">Ready to Schedule</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div>
              <label className={labelClasses}>Priority</label>
              <select
                className={inputClasses}
                value={formData.priority}
                onChange={(e) => updateSimpleField('priority', e.target.value)}
              >
                <option value="">Select Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="very high">Very High</option>
              </select>
            </div>
          </div>
        </div>

        {/* Client Information Section */}
        <div>
          <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Client Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="col-span-2">
              <label className={labelClasses}>Name</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter client name"
                value={formData.client.name}
                onChange={(e) => updateClient('name', e.target.value)}
              />
            </div>

            <div className="col-span-2">
              <label className={labelClasses}>Address</label>
              <textarea
                className={`${inputClasses} resize-none h-24`}
                placeholder="Enter client address"
                value={formData.client.address.street}
                onChange={(e) => updateClientAddress('street', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>City</label>
              <SearchableDropdown
                key="client-city-dropdown-nonTabbed"
                options={cityOptions}
                value={cityOptions.find(opt => opt.name === formData.client.address.city) || ''}
                onChange={handleClientCityChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                placeholder="Search city..."
                className={inputClasses}
                onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, clientCity: isEditable}))}
                isEditable={editableFields.clientCity}
              />
            </div>

            <div>
              <label className={labelClasses}>State</label>
              <SearchableDropdown
                key="client-state-dropdown-nonTabbed"
                options={US_STATES}
                value={formData.client.address.state}
                onChange={(value) => updateClientAddress('state', value)}
                placeholder="Enter state"
                className={inputClasses}
                onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, clientState: isEditable}))}
                isEditable={editableFields.clientState}
              />
            </div>

            <div>
              <label className={labelClasses}>Zip Code</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter zip code"
                value={formData.client.address.zipCode}
                onChange={(e) => updateClientAddress('zipCode', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Cell Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.client.cellPhone}
                onChange={(e) => updateClient('cellPhone', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Alt Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.client.altPhone}
                onChange={(e) => updateClient('altPhone', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Email</label>
              <input
                type="email"
                className={inputClasses}
                placeholder="Enter email address"
                value={formData.client.email}
                onChange={(e) => updateClient('email', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Billing Information Section */}
        <div>
          <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Billing Information</h2>
          
          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="same-billing-as-client"
                checked={sameBillingAsClient}
                onChange={handleSameBillingAsClientChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="same-billing-as-client" className="ml-2 text-sm text-gray-700">
                Same as client information
              </label>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="col-span-2">
              <label className={labelClasses}>Billing Contact Name</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter billing contact name"
                value={formData.billingContact.name}
                onChange={(e) => updateBillingContact('name', e.target.value)}
                disabled={sameBillingAsClient}
              />
            </div>
            
            <div>
              <label className={labelClasses}>Billing Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.billingContact.phone}
                onChange={(e) => updateBillingContact('phone', e.target.value)}
                disabled={sameBillingAsClient}
              />
            </div>
            
            <div>
              <label className={labelClasses}>Billing Email</label>
              <input
                type="email"
                className={inputClasses}
                placeholder="Enter billing email address"
                value={formData.billingContact.email}
                onChange={(e) => updateBillingContact('email', e.target.value)}
                disabled={sameBillingAsClient}
              />
            </div>
            
            <div className="col-span-2">
              <label className={labelClasses}>Billing Street Address</label>
              <textarea
                className={`${inputClasses} resize-none h-24`}
                placeholder="Enter billing address"
                value={formData.billingContact.address.street}
                onChange={(e) => updateBillingContactAddress('street', e.target.value)}
                disabled={sameBillingAsClient}
              />
            </div>
            
            <div>
              <label className={labelClasses}>City</label>
              <SearchableDropdown
                key="billing-city-dropdown-nonTabbed"
                options={cityOptions}
                value={cityOptions.find(opt => opt.name === formData.billingContact.address.city) || ''}
                onChange={(value) => updateBillingContactAddress('city', value)}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                placeholder="Search city..."
                className={inputClasses}
                disabled={sameBillingAsClient}
              />
            </div>
            
            <div>
              <label className={labelClasses}>State</label>
              <SearchableDropdown
                key="billing-state-dropdown-nonTabbed"
                options={US_STATES}
                value={formData.billingContact.address.state}
                onChange={(value) => updateBillingContactAddress('state', value)}
                placeholder="Enter state"
                className={inputClasses}
                disabled={sameBillingAsClient}
              />
            </div>
            
            <div>
              <label className={labelClasses}>Zip Code</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter zip code"
                value={formData.billingContact.address.zipCode}
                onChange={(e) => updateBillingContactAddress('zipCode', e.target.value)}
                disabled={sameBillingAsClient}
              />
            </div>
          </div>
        </div>

        {/* Site Contact Section */}
        <div>
          <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Site Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="col-span-2">
              <label className={labelClasses}>Site Contact Name</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter site contact name"
                value={formData.siteContact.name}
                onChange={(e) => updateSiteContact('name', e.target.value)}
              />
            </div>

            <div className="col-span-2">
              <label className={labelClasses}>Site Contact Address</label>
              <textarea
                className={`${inputClasses} resize-none h-24`}
                placeholder="Enter site contact address"
                value={formData.siteContact.address.street}
                onChange={(e) => updateSiteContactAddress('street', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Site Contact City</label>
              <SearchableDropdown
                key="site-contact-city-dropdown-nonTabbed"
                options={cityOptions}
                value={cityOptions.find(opt => opt.name === formData.siteContact.address.city) || ''}
                onChange={handleSiteContactCityChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                placeholder="Search city..."
                className={inputClasses}
                onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, siteContactCity: isEditable}))}
                isEditable={editableFields.siteContactCity}
              />
            </div>

            <div>
              <label className={labelClasses}>Site Contact State</label>
              <SearchableDropdown
                key="site-contact-state-dropdown-nonTabbed"
                options={US_STATES}
                value={formData.siteContact.address.state}
                onChange={(value) => updateSiteContactAddress('state', value)}
                placeholder="Enter state"
                className={inputClasses}
                onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, siteContactState: isEditable}))}
                isEditable={editableFields.siteContactState}
              />
            </div>

            <div>
              <label className={labelClasses}>Site Contact Zip Code</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter site contact zip code"
                value={formData.siteContact.address.zipCode}
                onChange={(e) => updateSiteContactAddress('zipCode', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Site Contact Cell Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.siteContact.cellPhone}
                onChange={(e) => updateSiteContact('cellPhone', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Site Contact Alt Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.siteContact.altPhone}
                onChange={(e) => updateSiteContact('altPhone', e.target.value)}
              />
            </div>

            <div>
              <label className={labelClasses}>Site Contact Email</label>
              <input
                type="email"
                className={inputClasses}
                placeholder="Enter site contact email"
                value={formData.siteContact.email}
                onChange={(e) => updateSiteContact('email', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Notes & Attachments Section */}
        <div>
          <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Notes & Attachments</h2>
          <div className="grid grid-cols-1 gap-y-6">
            <div className="min-h-[300px]">
              <label className={labelClasses}>Notes</label>
              <div className="h-full">
                <RichTextEditor
                  content={formData.notes}
                  onChange={(newContent) => setFormData(prev => ({
                    ...prev,
                    notes: newContent
                  }))}
                />
              </div>
            </div>

            <div>
              <label className={labelClasses}>Attachments</label>
              {formData.attachments && formData.attachments.length > 0 && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  {formData.attachments.map(attachment => renderAttachment(attachment))}
                </div>
              )}
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <div className="flex flex-col items-center justify-center">
                  <svg className="w-16 h-16 text-gray-400 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span className="text-sm text-gray-500">Select a file or drag and drop here</span>
                  <span className="text-xs text-gray-400 mt-1">(JPG, PNG or PDF file size no more than 10MB)</span>
                  <label htmlFor="file-upload-nonTabbed" className="mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 cursor-pointer">
                    SELECT FILE
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload-nonTabbed"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSave}
            className="px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none"
          >
            Save Changes
          </button>
        </div>
      </div>
    );
  };

  // Update the renderStepOne function to match LeadDetailsDialog style
  const renderStepOne = () => (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-center font-normal text-base sm:text-lg">Project Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div>
          <label className={labelClasses}>Job Number</label>
          <input
            type="text"
            className={inputClasses}
            value={formData.jobNumber}
            onChange={(e) => updateSimpleField('jobNumber', e.target.value)}
            readOnly
          />
        </div>

        <div>
          <label className={labelClasses}>Lead Contact Name</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter contact name"
            value={formData.leadContact.name}
            onChange={(e) => updateLeadContact('name', e.target.value)}
          />
        </div>
        
        <div>
          <label className={labelClasses}>Contact Phone</label>
          <input
            type="tel"
            className={inputClasses}
            placeholder="(000) 000-0000"
            value={formData.leadContact.phone}
            onChange={(e) => updateLeadContact('phone', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClasses}>Contact Email</label>
          <input
            type="email"
            className={inputClasses}
            placeholder="Enter email address"
            value={formData.leadContact.email}
            onChange={(e) => updateLeadContact('email', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClasses}>City</label>
          <SearchableDropdown
            key="project-city-dropdown-nonTabbed"
            options={cityOptions}
            value={cityOptions.find(opt => opt.name === formData.address.city) || ''}
            onChange={handleCityChange}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
            placeholder="Search city..."
            className={inputClasses}
            onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, city: isEditable}))}
            isEditable={editableFields.city}
          />
        </div>

        <div>
          <label className={labelClasses}>Assigned To</label>
          <MultiSelectDropdown
            selectedManagers={selectedManagers}
            onManagerSelect={handleManagerSelect}
            onManagerRemove={handleManagerRemove}
          />
        </div>

        <div>
          <label className={labelClasses}>Project Status</label>
          <select
            className={inputClasses}
            value={formData.projectStatus}
            onChange={(e) => updateSimpleField('projectStatus', e.target.value)}
          >
            <option value="">Select Status</option>
            <option value="active">Active</option>
            <option value="ready_to_schedule">Ready to Schedule</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <div>
          <label className={labelClasses}>Priority</label>
          <select
            className={inputClasses}
            value={formData.priority}
            onChange={(e) => updateSimpleField('priority', e.target.value)}
          >
            <option value="">Select Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="very high">Very High</option>
          </select>
        </div>

        <div>
          <label className={labelClasses}>Notes</label>
          <textarea
            className={textAreaClasses}
            placeholder="Enter any additional notes..."
            value={formData.notes}
            onChange={(e) => updateSimpleField('notes', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-center text-gray-800 mb-6">Client Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="col-span-2">
          <label className={labelClasses}>Client Name</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter client name"
            value={formData.client.name}
            onChange={(e) => updateClient('name', e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <label className={labelClasses}>Client Address</label>
          <textarea
            className={textAreaClasses}
            placeholder="Enter client address"
            value={formData.client.address.street}
            onChange={(e) => updateClientAddress('street', e.target.value)}
          />
        </div>
        <div>
          <label className={labelClasses}>City</label>
          <SearchableDropdown
            key="client-city-dropdown-nonTabbed"
            options={cityOptions}
            value={cityOptions.find(opt => opt.name === formData.client.address.city) || ''}
            onChange={handleClientCityChange}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
            placeholder="Search city..."
            className={inputClasses}
            onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, clientCity: isEditable}))}
            isEditable={editableFields.clientCity}
          />
        </div>
        <div>
          <label className={labelClasses}>State</label>
          <SearchableDropdown
            key="client-state-dropdown-nonTabbed"
            options={US_STATES}
            value={formData.client.address.state}
            onChange={(value) => updateClientAddress('state', value)}
            placeholder="Enter state"
            className={inputClasses}
            onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, clientState: isEditable}))}
            isEditable={editableFields.clientState}
          />
        </div>
        <div>
          <label className={labelClasses}>Zip Code</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter zip code"
            value={formData.client.address.zipCode}
            onChange={(e) => updateClientAddress('zipCode', e.target.value)}
          />
        </div>
        <div>
          <label className={labelClasses}>Phone</label>
          <input
            type="tel"
            className={inputClasses}
            placeholder="(000) 000-0000"
            value={formData.client.cellPhone}
            onChange={(e) => updateClient('cellPhone', e.target.value)}
          />
        </div>
        <div>
          <label className={labelClasses}>Alt Phone</label>
          <input
            type="tel"
            className={inputClasses}
            placeholder="(000) 000-0000"
            value={formData.client.altPhone}
            onChange={(e) => updateClient('altPhone', e.target.value)}
          />
        </div>
        <div>
          <label className={labelClasses}>Email</label>
          <input
            type="email"
            className={inputClasses}
            placeholder="Enter email address"
            value={formData.client.email}
            onChange={(e) => updateClient('email', e.target.value)}
          />
        </div>

        <div className="col-span-2 pt-6 border-t border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Billing Information</h2>
          
          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="same-billing-as-client-tabbed"
                checked={sameBillingAsClient}
                onChange={handleSameBillingAsClientChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="same-billing-as-client-tabbed" className="ml-2 text-sm text-gray-700">
                Same as client information
              </label>
            </div>
          </div>
        </div>

        <div className="col-span-2">
          <label className={labelClasses}>Billing Contact Name</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter billing contact name"
            value={formData.billingContact.name}
            onChange={(e) => updateBillingContact('name', e.target.value)}
            disabled={sameBillingAsClient}
          />
        </div>
        
        <div>
          <label className={labelClasses}>Billing Phone</label>
          <input
            type="tel"
            className={inputClasses}
            placeholder="(000) 000-0000"
            value={formData.billingContact.phone}
            onChange={(e) => updateBillingContact('phone', e.target.value)}
            disabled={sameBillingAsClient}
          />
        </div>
        
        <div>
          <label className={labelClasses}>Billing Email</label>
          <input
            type="email"
            className={inputClasses}
            placeholder="Enter billing email address"
            value={formData.billingContact.email}
            onChange={(e) => updateBillingContact('email', e.target.value)}
            disabled={sameBillingAsClient}
          />
        </div>
        
        <div className="col-span-2">
          <label className={labelClasses}>Billing Street Address</label>
          <textarea
            className={textAreaClasses}
            placeholder="Enter billing address"
            value={formData.billingContact.address.street}
            onChange={(e) => updateBillingContactAddress('street', e.target.value)}
            disabled={sameBillingAsClient}
          />
        </div>
        
        <div>
          <label className={labelClasses}>City</label>
          <SearchableDropdown
            key="billing-city-dropdown-nonTabbed"
            options={cityOptions}
            value={cityOptions.find(opt => opt.name === formData.billingContact.address.city) || ''}
            onChange={(value) => updateBillingContactAddress('city', value)}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
            placeholder="Search city..."
            className={inputClasses}
            disabled={sameBillingAsClient}
          />
        </div>
        
        <div>
          <label className={labelClasses}>State</label>
          <SearchableDropdown
            key="billing-state-dropdown-nonTabbed"
            options={US_STATES}
            value={formData.billingContact.address.state}
            onChange={(value) => updateBillingContactAddress('state', value)}
            placeholder="Enter state"
            className={inputClasses}
            disabled={sameBillingAsClient}
          />
        </div>
        
        <div>
          <label className={labelClasses}>Zip Code</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter zip code"
            value={formData.billingContact.address.zipCode}
            onChange={(e) => updateBillingContactAddress('zipCode', e.target.value)}
            disabled={sameBillingAsClient}
          />
        </div>
      </div>
    </div>
  );

  const renderStepThree = () => (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-center font-normal text-base sm:text-lg">Site Contact Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="col-span-2">
          <label className={labelClasses}>Site Contact Name</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter site contact name"
            value={formData.siteContact.name}
            onChange={(e) => updateSiteContact('name', e.target.value)}
          />
        </div>

        <div className="col-span-2">
          <label className={labelClasses}>Site Contact Address</label>
          <textarea
            className={`${inputClasses} resize-none h-24`}
            placeholder="Enter site contact address"
            value={formData.siteContact.address.street}
            onChange={(e) => updateSiteContactAddress('street', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClasses}>Site Contact City</label>
          <SearchableDropdown
            key="site-contact-city-dropdown-nonTabbed"
            options={cityOptions}
            value={cityOptions.find(opt => opt.name === formData.siteContact.address.city) || ''}
            onChange={handleSiteContactCityChange}
            getOptionLabel={(option) => option.label || ''}
            placeholder="Search city..."
            className={inputClasses}
            onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, siteContactCity: isEditable}))}
            isEditable={editableFields.siteContactCity}
          />
        </div>

        <div>
          <label className={labelClasses}>Site Contact State</label>
          <SearchableDropdown
            key="site-contact-state-dropdown-nonTabbed"
            options={US_STATES}
            value={formData.siteContact.address.state}
            onChange={(value) => updateSiteContactAddress('state', value)}
            placeholder="Enter state"
            className={inputClasses}
            onToggleEdit={(isEditable) => setEditableFields(prev => ({...prev, siteContactState: isEditable}))}
            isEditable={editableFields.siteContactState}
          />
        </div>

        <div>
          <label className={labelClasses}>Site Contact Zip Code</label>
          <input
            type="text"
            className={inputClasses}
            placeholder="Enter site contact zip code"
            value={formData.siteContact.address.zipCode}
            onChange={(e) => updateSiteContactAddress('zipCode', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClasses}>Site Contact Cell Phone</label>
          <input
            type="tel"
            className={inputClasses}
            placeholder="(000) 000-0000"
            value={formData.siteContact.cellPhone}
            onChange={(e) => updateSiteContact('cellPhone', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClasses}>Site Contact Alt Phone</label>
          <input
            type="tel"
            className={inputClasses}
            placeholder="(000) 000-0000"
            value={formData.siteContact.altPhone}
            onChange={(e) => updateSiteContact('altPhone', e.target.value)}
          />
        </div>

        <div>
          <label className={labelClasses}>Site Contact Email</label>
          <input
            type="email"
            className={inputClasses}
            placeholder="Enter site contact email"
            value={formData.siteContact.email}
            onChange={(e) => updateSiteContact('email', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // Add renderStepFour for Notes & Attachments
  const renderStepFour = () => (
    <div className="space-y-4">
      <h2 className="text-center font-normal text-base sm:text-lg">Notes & Attachments</h2>
      <div className="grid grid-cols-1 gap-y-6">
        <div className="min-h-[300px]">
          <label className={labelClasses}>Notes</label>
          <div className="h-full">
            <RichTextEditor
              content={formData.notes}
              onChange={(newContent) => setFormData(prev => ({
                ...prev,
                notes: newContent
              }))}
            />
          </div>
        </div>

        <div>
          <label className={labelClasses}>Attachments</label>
          {formData.attachments && formData.attachments.length > 0 && (
            <div className="grid grid-cols-4 gap-4 mb-4">
              {formData.attachments.map(attachment => renderAttachment(attachment))}
            </div>
          )}
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
            <div className="flex flex-col items-center justify-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-500">Select a file or drag and drop here</span>
              <span className="text-xs text-gray-400 mt-1">(JPG, PNG or PDF file size no more than 10MB)</span>
              <label htmlFor="file-upload" className="mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 cursor-pointer">
                SELECT FILE
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStepOne();
      case 2:
        return renderStepTwo();
      case 3:
        return renderStepThree();
      case 4:
        return renderStepFour();
      default:
        return null;
    }
  };

  const renderStepIndicator = () => {
    const steps = ['Project Info', 'Client Info', 'Site Contact', 'Notes & Files'];
    
    return (
      <div className="mb-6 sm:mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-2 sm:space-x-4 overflow-x-auto" aria-label="Tabs">
            {steps.map((step, index) => (
              <button
                key={step}
                onClick={() => setCurrentStep(index + 1)}
                className={`
                  whitespace-nowrap py-3 sm:py-4 px-3 sm:px-6 border-b-2 text-sm font-medium
                  ${currentStep === index + 1
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {step}
              </button>
            ))}
          </nav>
        </div>
      </div>
    );
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  // Update the renderChangeOrdersTab function
  const renderChangeOrdersTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-end mb-4">
          <button
            onClick={() => handleChangeOrderClick(null)}
            className="inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Create Change Order
          </button>
        </div>

        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projectChangeOrders.map((changeOrder) => (
                  <tr key={changeOrder._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {changeOrder.changeOrderId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {changeOrder.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${getStatusColor(changeOrder.status)}`}>
                        {formatStatus(changeOrder.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(changeOrder.clientPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(changeOrder.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={() => handleChangeOrderClick(changeOrder)}
                          className="text-indigo-600 hover:text-indigo-900 text-left"
                        >
                          Edit
                        </button>
                        {changeOrder.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleReleaseChangeOrder(changeOrder)}
                              className="text-green-600 hover:text-green-900 text-left"
                            >
                              Release
                            </button>
                            <button
                              onClick={() => handleDeleteChangeOrder(changeOrder)}
                              className="text-red-600 hover:text-red-900 text-left"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {projectChangeOrders.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No change orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Update the handleChangeOrderClick function
  const handleChangeOrderClick = async (changeOrder) => {
    try {
      if (changeOrder) {
        // Fetch the full change order data with populated items
        const result = await dispatch(fetchChangeOrder({
          projectId: project._id,
          changeOrderId: changeOrder._id
        })).unwrap();

        // Set the current change order in Redux store
        dispatch(setCurrentChangeOrder(result.changeOrder));
      } else {
        // Clear current change order for new creation
        dispatch(setCurrentChangeOrder(null));
      }
      setShowChangeOrder(true);
    } catch (error) {
      console.error('Failed to fetch change order details:', error);
      showToast('Failed to load change order details', 'error');
    }
  };

  // Add this helper function for status colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'released':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Add demo transactions data
  const [transactions] = useState([
    {
      id: 1,
      date: '2024-02-14',
      description: 'Initial Payment',
      amount: 5000.00,
      type: 'payment',
      status: 'approved'
    },
    {
      id: 2,
      date: '2024-02-15',
      description: 'Material Cost',
      amount: 2500.00,
      type: 'expense',
      status: 'pending'
    },
    {
      id: 3,
      date: '2024-02-16',
      description: 'Labor Invoice',
      amount: 1800.00,
      type: 'invoice',
      status: 'pending'
    },
    {
      id: 4,
      date: '2024-02-17',
      description: 'Equipment Rental',
      amount: 750.00,
      type: 'expense',
      status: 'rejected'
    },
    {
      id: 5,
      date: '2024-02-18',
      description: 'Progress Payment',
      amount: 3500.00,
      type: 'payment',
      status: 'approved'
    }
  ]);


  // Add these handlers
  const onEditDraft = (proposal) => {
    setEditingChangeOrder(proposal);
    setShowChangeOrder(true);
  };

  const onReleaseProposal = async (proposal) => {
    try {
      // You can dispatch an action to release the proposal
      // await dispatch(releaseProjectProposal(proposal._id)).unwrap();
      showToast('Proposal released successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to release proposal', 'error');
    }
  };

  const onDeleteProposal = async (proposal) => {
    if (window.confirm('Are you sure you want to delete this proposal?')) {
      try {
        // You can dispatch an action to delete the proposal
        // await dispatch(deleteProjectProposal(proposal._id)).unwrap();
        showToast('Proposal deleted successfully', 'success');
      } catch (error) {
        showToast(error.message || 'Failed to delete proposal', 'error');
      }
    }
  };

  const onCreateChangeOrder = async (proposal) => {
    try {
      const emailAddress = currentProject?.client?.email || proposal?.emailAddress || '';
      
      if (!emailAddress) {
        showToast('Client email address is required', 'error');
        return;
      }

      // Create initial change order data from proposal
      const changeOrderData = {
        title: `Change Order for ${currentProject.jobNumber || 'Project'}`,
        emailAddress,
        status: 'draft',
        category: proposal.category?._id,
        proposal: proposal._id,
        scopeOfWork: proposal.scopeOfWork?.map(item => ({
          costGroup: item.costGroup?._id,
          quantity: item.quantity || 1,
          unitCost: item.unitCost || 0,
          builderCost: item.builderCost || 0,
          markup: item.markup || 0,
          clientPrice: item.clientPrice || 0,
          margin: item.margin || 0
        })) || [],
        costCodes: proposal.costCodes?.map(item => ({
          costItem: item.costItem?._id,
          quantity: item.quantity || 1,
          unitCost: item.unitCost || 0,
          builderCost: item.builderCost || 0,
          markup: item.markup || 0,
          clientPrice: item.clientPrice || 0,
          margin: item.margin || 0
        })) || [],
        totalBuilderCost: proposal.totalBuilderCost || 0,
        totalClientPrice: proposal.clientPrice || 0,
        displaySettings: proposal.displaySettings || {
          showScopeDescription: true,
          showCostCodeDescription: true,
          showQuantity: false,
          showUnit: false,
          showPrice: true,
          showCostCodes: false,
          expandGroups: false,
          separateGroups: false
        }
      };

      // Create the change order
      const result = await dispatch(createChangeOrder({
        projectId: currentProject._id,
        changeOrderData
      })).unwrap();

      setEditingChangeOrder(result.changeOrder);
      setShowChangeOrder(true);
      showToast('Change order created successfully', 'success');
    } catch (error) {
      console.error('Failed to create change order:', error);
      showToast('Failed to create change order: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Update the handler
  const handleProposalClick = async (proposal) => {
    if (!proposal) {
      // Get the client email from the project
      const emailAddress = currentProject?.client?.email;
      
      if (!emailAddress) {
        showToast('Client email address is required', 'error');
        return;
      }

      // Create initial draft change order
      const initialChangeOrder = {
        title: `Change Order for ${currentProject.jobNumber || 'Project'}`,
        emailAddress, // Add this required field
        status: 'draft',
        items: [],
        totalClientPrice: 0,
        totalBuilderCost: 0
      };

      try {
        const result = await dispatch(createChangeOrder({
          projectId: currentProject._id,
          changeOrderData: initialChangeOrder
        })).unwrap();

        setEditingChangeOrder(result.changeOrder);
        setShowChangeOrder(true);
      } catch (error) {
        console.error('Failed to create initial change order:', error);
        showToast('Failed to create change order', 'error');
      }
    } else {
      setEditingChangeOrder(proposal);
      setShowChangeOrder(true);
    }
  };

  // Handler for releasing a change order
  const handleReleaseChangeOrder = async (changeOrder) => {
    try {
      await dispatch(updateChangeOrderStatus({
        projectId: currentProject._id,
        changeOrderId: changeOrder._id,
        status: 'released'
      })).unwrap();
      
      showToast('Change order released successfully', 'success');
    } catch (error) {
      console.error('Failed to release change order:', error);
      showToast('Failed to release change order: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Update the delete handler
  const handleDeleteChangeOrder = (changeOrder) => {
    setConfirmDialog({
      isOpen: true,
      changeOrder
    });
  };

  // Add the actual delete function
  const confirmDeleteChangeOrder = async () => {
    try {
      await dispatch(deleteChangeOrder({
        projectId: currentProject._id,
        changeOrderId: confirmDialog.changeOrder._id
      })).unwrap();
    } catch (error) {
      console.error('Failed to delete change order:', error);
      showToast('Failed to delete change order: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Add state for documents tab
  const [documents, setDocuments] = useState([]);

  // Add effect to load documents when tab changes
  useEffect(() => {
    if (activeTab === 'Documents' && currentProject?._id) {
      dispatch(fetchProjectDocuments(currentProject._id))
        .unwrap()
        .then(docs => setDocuments(docs))
        .catch(error => showToast(error, 'error'));
    }
  }, [activeTab, currentProject?._id, dispatch]);

  // Add renderDocumentsTab function
  const renderDocumentsTab = () => {
    return (
      <div className="space-y-4">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Modified
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {doc.fileName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${doc.type === 'Released Proposal' ? 'bg-blue-100 text-blue-800' : 
                        doc.type === 'Approved Proposal' ? 'bg-green-100 text-green-800' : 
                        'bg-gray-100 text-gray-800'}`}>
                      {doc.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(doc.size / 1024).toFixed(2)} KB
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(doc.lastModified).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <a 
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Show loading spinner while fetching project details
  if (loading) {
    return (
      <div className={`fixed inset-0 z-50 overflow-hidden bg-white flex justify-center items-center ${isMaximized ? '' : 'mx-auto my-16 max-w-7xl rounded-lg shadow-xl'}`}>
        <Spinner size="xl" color="blue" />
      </div>
    );
  }

  // Show error if no project data
  if (!currentProject) {
    return (
      <div className={`fixed inset-0 z-50 overflow-hidden bg-white ${isMaximized ? '' : 'mx-auto my-16 max-w-7xl rounded-lg shadow-xl'}`}>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-gray-500">No project data available</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Add a dedicated handler for the "Same as client" checkbox
  const handleSameBillingAsClientChange = () => {
    const newValue = !sameBillingAsClient;
    setSameBillingAsClient(newValue);
    
    if (newValue) {
      // If checkbox is being checked, copy client info to billing
      setFormData(prev => ({
        ...prev,
        billingContact: {
          name: prev.client?.name || '',
          phone: prev.client?.cellPhone || '',
          email: prev.client?.email || '',
          address: {
            street: prev.client?.address?.street || '',
            city: prev.client?.address?.city || '',
            state: prev.client?.address?.state || '',
            zipCode: prev.client?.address?.zipCode || ''
          }
        }
      }));
    }
  };

  return (
    <>
      {isOpen && (
        <div className={`fixed inset-0 z-50 overflow-hidden bg-white ${isMaximized ? '' : 'mx-auto my-16 max-w-7xl rounded-lg shadow-xl'}`}>
          {/* Dialog header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Project Details{project?.jobNumber ? ` - ${project.jobNumber}` : ''}</h2>
            <div className="flex items-center space-x-2">
              {/* View toggle icon - only show for General tab */}
              {activeTab === 'General' && (
                <button 
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                  onClick={toggleView}
                  title={isTabbedView ? "Switch to full view" : "Switch to tabbed view"}
                >
                  {isTabbedView ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  )}
                </button>
              )}
              
              {/* Maximize/restore icon */}
              <button 
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                onClick={toggleMaximize}
                title={isMaximized ? "Restore dialog size" : "Maximize dialog"}
              >
                {isMaximized ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
              
              {/* Close icon */}
              <button 
                className="text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-100 overflow-hidden">
            <div className="border-b overflow-x-auto">
              <nav className="flex gap-4 px-4 min-w-max">
                {['General', 'Change Orders', 'Financial', 'Documents', 'Permits'].map((tab) => (
                  <button
                    key={tab}
                    className={`py-4 px-2 border-b-2 font-medium ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4 h-[calc(100vh-160px)] overflow-y-auto">
              {activeTab === 'General' ? (
                isTabbedView ? (
                  <>
                    {renderStepIndicator()}
                    <div className="mb-8">
                      {renderStepContent()}
                    </div>
                    <div className="flex flex-col sm:flex-row justify-between gap-2 mt-8">
                      <div>
                        {currentStep > 1 && (
                          <button
                            type="button"
                            onClick={handleBack}
                            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Previous
                          </button>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={handleNext}
                          className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                        >
                          {currentStep === 4 ? 'Save' : 'Next'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  renderNonTabbedContent()
                )
              ) : activeTab === 'Change Orders' ? (
                renderChangeOrdersTab()
              ) : activeTab === 'Financial' ? (
                <FinancialTab project={currentProject} />
              ) : activeTab === 'Documents' ? (
                renderDocumentsTab()
              ) : (
                <PermitsTab projectId={currentProject._id} />
              )}
            </div>

            
          </div>
        </div>
      )}

      {showChangeOrder && (
        <ChangeOrderDialog
          isOpen={showChangeOrder}
          onClose={() => {
            setShowChangeOrder(false);
            dispatch(setCurrentChangeOrder(null));
          }}
          project={currentProject}
          currentChangeOrder={currentChangeOrder}
        />
      )}

      {showProposalDialog && (
        <ChangeOrderDialog
          isOpen={showProposalDialog}
          onClose={() => {
            setShowProposalDialog(false);
            setEditingProposal(null);
          }}
          project={currentProject}
          editingProposal={editingChangeOrder}
        />
      )}

      <ImagePreviewDialog
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, changeOrder: null })}
        onConfirm={confirmDeleteChangeOrder}
        title="Delete Change Order"
        message={`Are you sure you want to delete change order ${confirmDialog.changeOrder?.changeOrderId}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );
};

export default ProjectDetailsDialog; 